import React, { useState } from 'react';
import { getSessionControlVisibility } from '../sessionControlHelper.js';

/**
 * Panneau latéral droit reproduisant l'exact design, l'ordre des icônes, le décompte et le code couleur
 * de la session et de l'historique Android (RightPanel / StatusIcon / StatusIconWithTimer).
 * 
 * Ordre d'affichage (du haut vers le bas) :
 *  1. Mode de déplacement (Piéton, Vélo, Voiture)
 *  2. Corridor de sécurité (Shield) -> Uniquement si actif ou en alerte zone
 *  3. Détection d'arrêt (StatusIconWithTimer) -> Cyan au repos, Orange en décompte temps restant, Rouge si alerte arrêt
 *  4. MediaPanel :
 *     a. Audio (Microphone) -> Cyan par défaut, Rouge si alerte active
 *     b. Vidéo (Caméra)     -> Cyan par défaut, Rouge si alerte active
 *  5. DetectionPanel :
 *     a. Détection accident -> Cyan par défaut, Rouge si alerte ACCIDENT_DETECTED
 *     b. Touche raccourci   -> Cyan par défaut, Rouge si alerte SHORTCUT
 */
/** « jusqu'à 14:35 » pour une pause déclarée. */
const formatPauseEnd = (ms) => {
  const d = new Date(Number(ms));
  if (!ms || Number.isNaN(d.getTime())) return '';
  return ` jusqu’à ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const SessionControlPanel = ({
  sessionData,
  isHistoricalMode = false,
  onShowInfo = null
}) => {
  const [tooltip, setTooltip] = useState(null);

  if (!sessionData) return null;

  const {
    travelMode,
    corridorSizeMeters,
    stopDetectionDurationMs,
    isStopDetectionEnabled,
    isStopped,
    stopTimerValue,
    shortcutDetectionMode,
    sportPauseUntil,
    sportCheck,
  } = sessionData;

  const visibility = getSessionControlVisibility(sessionData);
  const { isAlert, trigger: activeTrigger } = visibility;

  // Couleurs standards du thème Android (ThemeAppColorPalette & Material Theme)
  const primaryColor = "#00BCD4"; // Bleu Cyan primaire (Material Theme MGA_Cyan_Secondary)
  const errorColor = "#D32F2F";   // Rouge Alerte (MGA_Alert_Red)
  const warningColor = "#FF9800"; // Orange Décompte / Warning (ThemeState.warningOrange)

  // Parsing booléen sécurisé
  const isStopDetectionEnabledBool = isStopDetectionEnabled === true || isStopDetectionEnabled === 'true';
  const isStoppedBool = isStopped === true || isStopped === 'true';

  // SVGs identiques aux Material Icons Android
  const walkSvg = (
    <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
      <path d="M13.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM9.8 8.9L7 23h2.1l1.8-8 2.1 2v6h2v-7.5l-2.1-2 .6-3C14.8 12 16.8 13 19 13v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1L6 8.3V13h2V9.6l1.8-.7"/>
    </svg>
  );

  const bikeSvg = (
    <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
      <path d="M15.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM5 12c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zm0 8.5c-1.9 0-3.5-1.6-3.5-3.5s1.6-3.5 3.5-3.5 3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5zm5.8-10l2.4-2.4.8.8c1.3 1.3 3 2.1 5 2.1V9c-1.5 0-2.9-.6-3.9-1.6l-1.7-1.7c-.4-.4-.9-.7-1.5-.7s-1.1.3-1.5.7L6.5 9.6c-.4.4-.5.9-.5 1.4 0 .6.2 1.1.7 1.5L9 14.8V19h2v-5l-1.9-1.9 1.7-1.7zM19 12c-2.8 0-5 2.2-5 5s2.2 5 5 5 5-2.2 5-5-2.2-5-5-5zm0 8.5c-1.9 0-3.5-1.6-3.5-3.5s1.6-3.5 3.5-3.5 3.5 1.6 3.5 3.5-1.6 3.5-3.5 3.5z"/>
    </svg>
  );

  const carSvg = (
    <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
      <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
    </svg>
  );

  const shieldSvg = (
    <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
    </svg>
  );

  const micSvg = (
    <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
      <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.91-3c-.49 0-.9.36-.98.85C16.52 14.2 14.47 16 12 16s-4.52-1.8-4.93-4.15c-.08-.49-.49-.85-.98-.85-.61 0-1.09.54-1 1.14.49 3 2.89 5.35 5.91 5.78V20c0 .55.45 1 1 1s1-.45 1-1v-2.08c3.02-.43 5.42-2.78 5.91-5.78.1-.6-.39-1.14-1-1.14z"/>
    </svg>
  );

  const videoSvg = (
    <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
      <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
    </svg>
  );

  const carCrashSvg = (
    <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
      <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.22.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.85 7h10.29l1.04 3H5.81l1.04-3zM18 14.5c0 .83-.67 1.5-1.5 1.5s-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm-10.5 0c0 .83-.67 1.5-1.5 1.5S4.5 15.33 4.5 14.5 5.17 13 6 13s1.5.67 1.5 1.5z"/>
    </svg>
  );

  const powerSvg = (
    <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
      <path d="M13 3h-2v10h2V3zm4.83 2.17l-1.42 1.42C17.99 7.86 19 9.81 19 12c0 3.87-3.13 7-7 7s-7-3.13-7-7c0-2.19 1.01-4.14 2.58-5.42L6.17 5.17C4.23 6.82 3 9.26 3 12c0 4.97 4.03 9 9 9s9-4.03 9-9c0-2.74-1.23-5.18-3.17-6.83z"/>
    </svg>
  );

  const touchAppSvg = (
    <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24" fill="currentColor">
      <path d="M9 11.24V7.5C9 6.12 10.12 5 11.5 5S14 6.12 14 7.5v3.74c1.21-.81 2-2.18 2-3.74C16 5.01 13.99 3 11.5 3S7 5.01 7 7.5c0 1.56.79 2.93 2 3.74zm9.84 4.63l-4.54-2.26c-.17-.07-.35-.11-.54-.11H13v-6c0-.83-.67-1.5-1.5-1.5S10 6.67 10 7.5v10.74l-3.43-.72c-.08-.01-.15-.02-.24-.02-.31 0-.59.13-.79.33l-.79.8 4.94 4.94c.27.27.65.43 1.06.43h6.79c.75 0 1.33-.55 1.44-1.28l.75-5.27c.01-.07.02-.14.02-.2 0-.62-.38-1.16-.95-1.39z"/>
    </svg>
  );

  const applicationSosSvg = (
    <svg xmlns="http://www.w3.org/2000/svg" height="26" viewBox="0 0 24 24" width="26" fill="currentColor">
      <path d="M17 1.01 7 1C5.9 1 5 1.9 5 3v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"/>
    </svg>
  );

  const getTravelModeIcon = () => {
    switch (travelMode) {
      case 'DRIVE':
      case 'CAR':
        return carSvg;
      case 'BICYCLE':
      case 'BIKE':
      case 'TWO_WHEELER':
        return bikeSvg;
      case 'HIKE':
      case 'RUN':
      case 'WALK':
      default:
        return walkSvg;
    }
  };

  const getTravelModeName = () => {
    switch (travelMode) {
      case 'DRIVE':
      case 'CAR':
        return 'Voiture';
      case 'BICYCLE':
      case 'BIKE':
      case 'TWO_WHEELER':
        return 'Vélo';
      case 'HIKE':
        return 'Randonnée';
      case 'RUN':
        return 'Course à pied';
      case 'WALK':
      default:
        return 'Piéton';
    }
  };

  // Formatage identique à formatMillisecondsToDisplay dans StatusIconWithTimer.kt
  const formatMillisecondsToDisplay = (ms) => {
    if (!ms || ms <= 0) return '0s';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes > 0 && seconds > 0) return `${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m`;
    return `${totalSeconds}s`;
  };

  // 1. Détection d'arrêt (StatusIconWithTimer.kt)
  const isStopDetectionAlerting = activeTrigger === 'ITINERARY_STOPPED' || activeTrigger === 'LIVE_SHARE_STOPPED';
  const thresholdToUse = Number(stopDetectionDurationMs) || 120000;
  const currentTimerVal = Number(stopTimerValue) || 0;
  const isCountingDown = isStoppedBool && !isAlert && currentTimerVal > 0;
  const remainingTimeMs = Math.max(0, thresholdToUse - currentTimerVal);

  const stopDisplayText = isCountingDown
    ? formatMillisecondsToDisplay(remainingTimeMs)
    : formatMillisecondsToDisplay(thresholdToUse);

  const stopTextColor = !isStopDetectionEnabledBool
    ? 'rgba(26, 26, 26, 0.4)'
    : isStopDetectionAlerting
      ? errorColor
      : isStoppedBool && !isAlert
        ? warningColor
        : primaryColor;

  // 2. Visibilité calquée sur Android RightPanel.kt : pendant une alerte,
  // seulement les médias actifs et l'élément déclencheur restent visibles.
  const showTravelModeButton = visibility.travel;
  const isCorridorAlerting = activeTrigger === 'ITINERARY_OUT_OF_ZONE';
  const showCorridorButton = visibility.corridor;
  const showStopDetectionButton = visibility.stop;
  const isAccidentAlerting = activeTrigger === 'ACCIDENT_DETECTED';
  const showAccidentButton = visibility.accident;
  const isShortcutAlerting = activeTrigger === 'SHORTCUT';
  const showShortcutButton = visibility.shortcut;
  const showApplicationButton = visibility.application;

  // Média (MediaPanel.kt) : Audio en premier, puis Vidéo.
  const showAudioButton = visibility.audio;
  const showVideoButton = visibility.video;
  const mediaColor = isAlert ? errorColor : primaryColor;

  return (
    <div
      className="session-control-panel"
      style={{
        position: 'absolute',
        top: '16px',
        right: '16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        zIndex: 500,
        pointerEvents: 'auto',
      }}
    >
      {/* 0. Sortie Sport : question « Tout va bien ? » en attente, ou pause déclarée */}
      {!isAlert && (sportCheck || sportPauseUntil) && (
        <div
          className="sport-status-pill"
          style={{
            alignSelf: 'flex-end',
            maxWidth: '220px',
            padding: '6px 10px',
            borderRadius: '14px',
            backgroundColor: warningColor,
            color: '#1A1A1A',
            fontSize: '12px',
            fontWeight: 700,
            boxShadow: '0 4px 8px rgba(0,0,0,0.25)',
            textAlign: 'right',
          }}
        >
          {sportCheck
            ? '« Tout va bien ? » demandé, en attente de réponse'
            : `Pause déclarée${formatPauseEnd(sportPauseUntil)}`}
        </div>
      )}

      {/* 1. Mode de transport */}
      {showTravelModeButton && (
        <button
          type="button"
          className="control-icon-btn"
          title={`Mode de déplacement : ${getTravelModeName()}`}
          onClick={() => setTooltip(`Mode de déplacement : ${getTravelModeName()}`)}
          style={{
            backgroundColor: '#ffffff',
            color: primaryColor,
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            border: `2px solid ${primaryColor}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 8px rgba(0,0,0,0.25)',
            cursor: 'default',
          }}
        >
          {getTravelModeIcon()}
        </button>
      )}

      {/* 2. Corridor de sécurité */}
      {showCorridorButton && (
        <button
          type="button"
          className="control-icon-btn"
          title={`Corridor de sécurité actif (${corridorSizeMeters || 30}m)`}
          onClick={() => setTooltip(`Corridor de sécurité actif (${corridorSizeMeters || 30}m)`)}
          style={{
            backgroundColor: '#ffffff',
            color: isCorridorAlerting ? errorColor : primaryColor,
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            border: `2px solid ${isCorridorAlerting ? errorColor : primaryColor}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 8px rgba(0,0,0,0.25)',
            cursor: 'default',
          }}
        >
          {shieldSvg}
        </button>
      )}

      {/* 3. Détection d'arrêt (StatusIconWithTimer) */}
      {showStopDetectionButton && (
        <button
          type="button"
          className="control-icon-btn"
          title={`Détection d'arrêt (${formatMillisecondsToDisplay(thresholdToUse)}) : ${isCountingDown ? `Temps restant : ${stopDisplayText}` : isStoppedBool ? 'Arrêt détecté' : 'Actif'}`}
          onClick={() => setTooltip(`Détection d'arrêt (${formatMillisecondsToDisplay(thresholdToUse)}) : ${isCountingDown ? `Temps restant : ${stopDisplayText}` : isStoppedBool ? 'Arrêt détecté' : 'Actif'}`)}
          style={{
            backgroundColor: '#ffffff',
            color: stopTextColor,
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            border: `2px solid ${stopTextColor}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 8px rgba(0,0,0,0.25)',
            cursor: 'default',
            fontWeight: 'bold',
            fontSize: isCountingDown ? '0.78rem' : '0.85rem',
            transition: 'color 0.2s ease, border-color 0.2s ease',
          }}
        >
          {stopDisplayText}
        </button>
      )}

      {/* 4a. Média Audio (Microphone) - En premier dans MediaPanel */}
      {showAudioButton && (
        <button
          type="button"
          className="control-icon-btn"
          title="Enregistrement audio activé"
          onClick={() => setTooltip('Enregistrement audio activé pendant la session')}
          style={{
            backgroundColor: '#ffffff',
            color: mediaColor,
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            border: `2px solid ${mediaColor}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 8px rgba(0,0,0,0.25)',
            cursor: 'default',
          }}
        >
          {micSvg}
        </button>
      )}

      {/* 4b. Média Vidéo (Caméra) - En second dans MediaPanel */}
      {showVideoButton && (
        <button
          type="button"
          className="control-icon-btn"
          title="Enregistrement vidéo activé"
          onClick={() => setTooltip('Enregistrement vidéo activé pendant la session')}
          style={{
            backgroundColor: '#ffffff',
            color: mediaColor,
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            border: `2px solid ${mediaColor}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 8px rgba(0,0,0,0.25)',
            cursor: 'default',
          }}
        >
          {videoSvg}
        </button>
      )}

      {/* 5. Détection accident */}
      {showAccidentButton && (
        <button
          type="button"
          className="control-icon-btn"
          title="Détection des accidents de circulation active"
          onClick={() => setTooltip('Détection des accidents de circulation active')}
          style={{
            backgroundColor: '#ffffff',
            color: isAccidentAlerting ? errorColor : primaryColor,
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            border: `2px solid ${isAccidentAlerting ? errorColor : primaryColor}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 8px rgba(0,0,0,0.25)',
            cursor: 'default',
          }}
        >
          {carCrashSvg}
        </button>
      )}

      {/* 6. Touche raccourci physique (Power ou Volume) */}
      {showShortcutButton && (
        <button
          type="button"
          className="control-icon-btn"
          title={`Touche raccourci (${shortcutDetectionMode === 'POWER' ? 'Bouton Power' : 'Touches Volume'})`}
          onClick={() => setTooltip(`Touche raccourci (${shortcutDetectionMode === 'POWER' ? 'Bouton Power' : 'Touches Volume'})`)}
          style={{
            backgroundColor: '#ffffff',
            color: isShortcutAlerting ? errorColor : primaryColor,
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            border: `2px solid ${isShortcutAlerting ? errorColor : primaryColor}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 8px rgba(0,0,0,0.25)',
            cursor: 'default',
          }}
        >
          {shortcutDetectionMode === 'POWER' ? powerSvg : touchAppSvg}
        </button>
      )}

      {/* Déclenchement manuel depuis l'application */}
      {showApplicationButton && (
        <button
          type="button"
          className="control-icon-btn"
          title="Alerte déclenchée depuis l'application"
          onClick={() => setTooltip("Alerte déclenchée depuis l'application")}
          style={{
            backgroundColor: '#ffffff', color: errorColor, width: '48px', height: '48px',
            borderRadius: '50%', border: `2px solid ${errorColor}`, display: 'flex',
            alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 8px rgba(0,0,0,0.25)',
            cursor: 'default',
          }}
        >
          {applicationSosSvg}
        </button>
      )}

      {/* Tooltip interactif au clic */}
      {tooltip && (
        <div
          style={{
            position: 'absolute',
            top: '0',
            right: '58px',
            whiteSpace: 'nowrap',
            backgroundColor: 'rgba(10, 25, 47, 0.95)',
            color: '#ffffff',
            padding: '8px 14px',
            borderRadius: '8px',
            fontSize: '0.85rem',
            fontWeight: '600',
            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255,255,255,0.15)',
            animation: 'fadeIn 0.2s ease',
          }}
          onClick={() => setTooltip(null)}
        >
          {tooltip}
        </div>
      )}
    </div>
  );
};

export default SessionControlPanel;
